import { message } from 'antd'
import {
  CATCH_BOARD_DEAL_NAME_MAX_LENGTH,
  CATCH_BOARD_DESCRIPTION_MAX_LENGTH,
  CATCH_BOARD_LEAD_SOURCE,
  CATCH_BOARD_LEAD_SOURCE_DETAIL_MAX_LENGTH,
  CATCH_BOARD_MAX_LINKED_OPPORTUNITIES,
} from 'src/screens/catch-board/catchBoardConstants'
import { fetchCatchBoardDuplicateVenueWarnings } from 'src/utils/catchBoardApi'
import { normalizeCatchBoardBuyers } from 'src/utils/catchBoardBuyerHelpers'
import {
  formatCatchBoardDuplicateVenueAlsoOn,
  hasCatchBoardDuplicateVenueWarnings,
} from 'src/utils/catchBoardDuplicateVenues'
import { buildEmptyCatchBoardDealForm } from 'src/utils/catchBoardFormHelpers'

function truncate(value, max) {
  return String(value || '').trim().slice(0, max)
}

function venueDisplayName(venue = {}) {
  return String(venue.name || venue.venueName || '').trim()
}

function meaningfulText(value) {
  const text = String(value || '').trim()
  if (!text || text === '—') {
    return ''
  }
  return text
}

function buildSpartacusLeadSourceDetail(venue = {}) {
  const plan = String(venue.plan || '').trim()
  return truncate(
    plan ? `Plan: ${plan}` : 'Spartacus venue table',
    CATCH_BOARD_LEAD_SOURCE_DETAIL_MAX_LENGTH,
  )
}

function buildSpartacusHandoffDescription(venue = {}) {
  const parts = ['Handed off from Spartacus.']
  if (venue.plan) {
    parts.push(`Plan: ${venue.plan}.`)
  }
  if (venue.upgradeScore != null && venue.upgradeScore !== '') {
    parts.push(`Upgrade score: ${venue.upgradeScore}.`)
  }
  if (venue.ordersInPeriod != null && venue.ordersInPeriod !== '') {
    parts.push(`Orders in period: ${venue.ordersInPeriod}.`)
  }
  return truncate(parts.join(' '), CATCH_BOARD_DESCRIPTION_MAX_LENGTH)
}

function opportunitySignalLabel(row = {}) {
  return [row.typeLabel, row.featureLabel].filter(meaningfulText).join(' · ')
}

function opportunityVenueName(row = {}) {
  return meaningfulText(row.venueName)
}

function contactFromOpportunityRow(row = {}) {
  const label = meaningfulText(row.contactLabel)
  const roleMatch = label.match(/\(([^)]+)\)\s*$/)
  const name = label.replace(/\s*\([^)]*\)\s*$/, '').trim()
  return {
    name,
    email: String(row.contactEmail || '').trim(),
    phone: String(row.contactPhone || '').trim(),
    role: roleMatch ? String(roleMatch[1]).trim() : '',
  }
}

function buildCrowsNestLeadSourceDetail(row = {}) {
  const signal = opportunitySignalLabel(row)
  return truncate(
    signal || "Crow's Nest opportunity",
    CATCH_BOARD_LEAD_SOURCE_DETAIL_MAX_LENGTH,
  )
}

function buildCrowsNestHandoffDescription(row = {}) {
  const parts = ["Handed off from Crow's Nest."]
  const signal = opportunitySignalLabel(row)
  if (signal) {
    parts.push(`Signal: ${signal}.`)
  }
  const plan = meaningfulText(row.plan)
  if (plan) {
    parts.push(`Plan: ${plan}.`)
  }
  const why = meaningfulText(row.whyFired)
  if (why) {
    parts.push(`Why: ${why}.`)
  }
  const followUp = meaningfulText(row.suggestedFollowUp)
  if (followUp) {
    parts.push(`Follow-up: ${followUp}.`)
  }
  return truncate(parts.join(' '), CATCH_BOARD_DESCRIPTION_MAX_LENGTH)
}

/**
 * Pre-fill a Catch Board create form from a Spartacus venue row.
 * @param {object} venue
 * @param {{ accountId?: string|null }} [options]
 */
export function buildCatchBoardDealFormFromSpartacusVenue(
  venue = {},
  { accountId = null } = {},
) {
  const form = buildEmptyCatchBoardDealForm({ accountId })
  const name = truncate(venueDisplayName(venue), CATCH_BOARD_DEAL_NAME_MAX_LENGTH)
  const venueId = venue.venueId ? String(venue.venueId) : ''

  return {
    ...form,
    name,
    // Venue title is not a company/group — leave blank for the salesperson.
    companyName: '',
    description: buildSpartacusHandoffDescription(venue),
    leadSource: CATCH_BOARD_LEAD_SOURCE.SPARTACUS,
    leadSourceDetail: buildSpartacusLeadSourceDetail(venue),
    linkedVenues: venueId
      ? [{ venueId, name: name || 'Venue' }]
      : [],
    primaryVenueId: venueId || null,
  }
}

/**
 * Pre-fill a Catch Board create form from a Crow's Nest opportunity row.
 * @param {object} row — mapped feed item
 * @param {{ accountId?: string|null }} [options]
 */
export function buildCatchBoardDealFormFromCrowsNestOpportunity(
  row = {},
  { accountId = null } = {},
) {
  const form = buildEmptyCatchBoardDealForm({ accountId })
  const name = truncate(
    opportunityVenueName(row),
    CATCH_BOARD_DEAL_NAME_MAX_LENGTH,
  )
  const venueId = row.venueId ? String(row.venueId) : ''
  const opportunityId = row.id ? String(row.id) : ''
  const contact = contactFromOpportunityRow(row)

  return {
    ...form,
    name,
    // Venue title is not a company/group — leave blank for the salesperson.
    companyName: '',
    description: buildCrowsNestHandoffDescription(row),
    leadSource: CATCH_BOARD_LEAD_SOURCE.CROWS_NEST,
    leadSourceDetail: buildCrowsNestLeadSourceDetail(row),
    linkedVenues: venueId
      ? [{ venueId, name: name || 'Venue' }]
      : [],
    primaryVenueId: venueId || null,
    linkedOpportunityIds: opportunityId
      ? [opportunityId].slice(0, CATCH_BOARD_MAX_LINKED_OPPORTUNITIES)
      : [],
    userBuyers: normalizeCatchBoardBuyers([contact]),
  }
}

/**
 * Warn (do not block) when handing a venue off to Catch Board if it is already
 * on another open deal. Used by Spartacus / Crow’s Nest before the create modal.
 *
 * @param {{
 *   venueId?: string|null,
 *   venueName?: string|null,
 * }} [options]
 * @returns {Promise<Array<object>>}
 */
export async function warnCatchBoardHandoffDuplicateVenue({
  venueId = null,
  venueName = null,
} = {}) {
  const id = String(venueId || '').trim()
  if (!id) {
    return []
  }

  try {
    const warnings = await fetchCatchBoardDuplicateVenueWarnings({
      venueIds: [id],
    })
    if (!hasCatchBoardDuplicateVenueWarnings(warnings)) {
      return warnings
    }

    const warning =
      warnings.find(row => String(row.venueId) === id) || warnings[0]
    const label = String(venueName || '').trim() || `Venue ${id}`
    message.warning(
      `${label}: ${formatCatchBoardDuplicateVenueAlsoOn(warning)}`,
      6,
    )
    return warnings
  } catch {
    // Warn-only — handoff still proceeds if the check fails.
    return []
  }
}
